import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ProductTourId =
  | "dashboard" | "inbox" | "chat" | "alerts" | "flows" | "campaigns"
  | "autopilot" | "pulse" | "tasks" | "sales" | "settings";

export type FirstWinChannel = "whatsapp";

// Walkthroughs open by themselves only in workspaces that finished onboarding
// in this browser, so existing users never see them uninvited. Anyone can
// replay one from a page's help button.
export const useProductTourStore = create<{
  eligible: Record<string, true>;
  seen: Record<string, ProductTourId[]>;
  active: { orgId: string; tourId: ProductTourId; step: number; direction: 1 | -1 } | null;
  // Opened on arrival at its page even if already seen, e.g. after "Show me the Inbox".
  queued: { orgId: string; tourId: ProductTourId } | null;
  // A first-win dialog shown after onboarding holds walkthroughs back until it closes.
  firstWin: { orgId: string; channel: FirstWinChannel } | null;
  markEligible: (orgId: string) => void;
  start: (orgId: string, tourId: ProductTourId) => void;
  queue: (orgId: string, tourId: ProductTourId) => void;
  setStep: (step: number, direction: 1 | -1) => void;
  finish: () => void;
  showFirstWin: (orgId: string, channel: FirstWinChannel) => void;
  closeFirstWin: () => void;
}>()(persist((set) => ({
  eligible: {},
  seen: {},
  active: null,
  queued: null,
  firstWin: null,
  markEligible: (orgId) => set((state) => ({ eligible: { ...state.eligible, [orgId]: true } })),
  start: (orgId, tourId) => set({ active: { orgId, tourId, step: 0, direction: 1 }, queued: null }),
  queue: (orgId, tourId) => set({ queued: { orgId, tourId } }),
  setStep: (step, direction) => set((state) => ({ active: state.active && { ...state.active, step, direction } })),
  finish: () => set((state) => {
    if (!state.active) return {};
    const { orgId, tourId } = state.active;
    const seen = state.seen[orgId] ?? [];
    return { active: null, seen: { ...state.seen, [orgId]: seen.includes(tourId) ? seen : [...seen, tourId] } };
  }),
  showFirstWin: (orgId, channel) => set({ firstWin: { orgId, channel } }),
  closeFirstWin: () => set({ firstWin: null }),
}), {
  name: "product-tours",
  partialize: (state) => ({ eligible: state.eligible, seen: state.seen, firstWin: state.firstWin }),
}));
